import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBlogs } from "../../../../api/blogs";
import { resolveBlogImageSrc } from "../../../../utils/blogImages";
import { resolveBlogPdfUrl, blogPdfFileName } from "../../../../utils/blogPdf";
import { sanitizeBlogHtml } from "../../../../utils/blogHtml";
import { readingTimeLabelFromBlog } from "../../../../utils/readingTime";
import { isBlogPublished } from "../../../../utils/blogStatus";
import "./PublicBlogDetail.css";
import { FaArrowLeft } from "react-icons/fa";

const PublicBlogDetail = () => {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchBlog = async () => {
            try {
                const data = await getBlogs();
                if (cancelled) return;

                const list = Array.isArray(data) ? data : [];
                const target = list.find((item) => item?.slug === slug || item?._id === slug);

                if (!target || !isBlogPublished(target)) {
                    setError("Blog not found.");
                } else {
                    setBlog(target);
                }
            } catch (err) {
                if (cancelled) return;
                const msg = err?.response?.data?.message || err?.message || "Failed to load blog.";
                setError(msg);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchBlog();

        return () => {
            cancelled = true;
        };
    }, [slug]);

    const imageSrc = useMemo(() => {
        if (!blog?.image) return null;
        return resolveBlogImageSrc(blog.image) || blog.image;
    }, [blog?.image]);

    const pdfSrc = useMemo(() => resolveBlogPdfUrl(blog?.pdfUrl), [blog?.pdfUrl]);

    const createdLabel = useMemo(() => {
        if (!blog?.createdAt) return "";
        const date = new Date(blog.createdAt);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    }, [blog?.createdAt]);

    const readingTime = useMemo(() => {
        if (!blog) return "1 min read";
        return readingTimeLabelFromBlog(blog);
    }, [blog]);

    const authorLabel = useMemo(() => {
        return String(blog?.author || "Offshore Advisory Editorial");
    }, [blog?.author]);

    if (loading) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details loading">
                        <div className="pb-topbar-skeleton" />
                        <div className="pb-hero-skeleton" />
                        <div className="pb-panel-skeleton" />
                    </section>
                </div>
            </main>
        );
    }

    if (error) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details error">
                        <div className="pb-state error" role="alert">
                            <h2 className="pb-state-title">Could not load this blog</h2>
                            <p className="pb-state-text">{error}</p>
                            <Link className="pb-link-back" to="/blogs"><FaArrowLeft /> Back to Blog List</Link>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    if (!blog) {
        return (
            <main className='main'>
                <div className="section pb-section container">
                    <section className="pb" aria-label="Blog details empty">
                        <div className="pb-state">
                            <h2 className="pb-state-title">Blog not found</h2>
                            <p className="pb-state-text">The requested post does not exist or may have been removed.</p>
                            <Link className="pb-link-back" to="/blogs">Back to Blog List</Link>
                        </div>
                    </section>
                </div>
            </main>

        );
    }

    return (
        <main className='main'>
            <div className="section pb-section container">
                <section className=" pb" aria-label="Blog details">
                    <div className="pb-topbar">
                        <Link to="/blogs" className="pb-link-back"><FaArrowLeft /> Back to Blog List</Link>
                    </div>

                    <article className="pb-article">
                        <header className="pb-hero">
                            {imageSrc ? (
                                <img
                                    className="pb-hero-image"
                                    src={imageSrc}
                                    alt={blog?.title || "Blog cover"}
                                />
                            ) : (
                                <div className="pb-hero-placeholder" aria-hidden="true" />
                            )}

                            <div className="pb-hero-overlay" />

                            <div className="pb-hero-content">
                                <h1 className="pb-title">{blog?.title || "Blog"}</h1>

                                <div className="pb-meta-row">
                                    <span className="pb-chip">By {authorLabel}</span>
                                    {createdLabel ? <span className="pb-chip">{createdLabel}</span> : null}
                                    <span className="pb-chip">{readingTime}</span>
                                </div>
                            </div>
                        </header>

                        {blog?.intro ? (
                            <section className="pb-intro" aria-label="Blog intro">
                                <p className="pb-intro-text">{blog.intro}</p>
                            </section>
                        ) : null}

                        {pdfSrc ? (
                            <section className="pb-pdf pb-pdf--name-only" aria-label="Attached PDF document">
                                <a
                                    className="pb-pdf-name-link"
                                    href={pdfSrc}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {blogPdfFileName(blog?.pdfUrl)}
                                </a>
                            </section>
                        ) : null}

                        <section className="pb-content-wrap" aria-label="Blog content">
                            <div
                                className="pb-content"
                                dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(blog?.content || "") }}
                            />
                        </section>
                    </article>
                </section>
            </div>
        </main>
    );
};

export default PublicBlogDetail;
